import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { forwardRef, useEffect, useCallback, useRef } from 'react';
import { createPortal } from 'react-dom';
import { X } from 'lucide-react';
import { cn } from '../utils/cn';
export const ModalOverlay = forwardRef(({ className, ...props }, ref) => {
    return (_jsx("div", { ref: ref, className: cn('absolute inset-0 bg-dark-900/60 dark:bg-black/70', 'animate-overlay-in', className), "aria-hidden": "true", ...props }));
});
ModalOverlay.displayName = 'ModalOverlay';
const sizeClasses = {
    sm: 'max-w-sm',
    md: 'max-w-lg',
    lg: 'max-w-2xl',
    xl: 'max-w-4xl',
    full: 'max-w-[calc(100vw-2rem)] max-h-[calc(100vh-2rem)]',
};
export const Modal = ({ open, onClose, children, size = 'md', closeOnOverlayClick = true, closeOnEscape = true, showCloseButton = true, className, 'aria-label': ariaLabel, 'aria-labelledby': ariaLabelledBy, }) => {
    const panelRef = useRef(null);
    const previousActiveElement = useRef(null);
    // Handle Escape key
    const handleKeyDown = useCallback((event) => {
        if (closeOnEscape && event.key === 'Escape') {
            event.stopPropagation();
            onClose();
        }
    }, [closeOnEscape, onClose]);
    // Handle overlay click
    const handleOverlayClick = useCallback((event) => {
        if (closeOnOverlayClick && event.target === event.currentTarget) {
            onClose();
        }
    }, [closeOnOverlayClick, onClose]);
    // Manage focus and body scroll
    useEffect(() => {
        if (open) {
            previousActiveElement.current = document.activeElement;
            document.addEventListener('keydown', handleKeyDown);
            document.body.style.overflow = 'hidden';
            // Focus the panel
            requestAnimationFrame(() => {
                panelRef.current?.focus();
            });
            return () => {
                document.removeEventListener('keydown', handleKeyDown);
                document.body.style.overflow = '';
                previousActiveElement.current?.focus();
            };
        }
    }, [open, handleKeyDown]);
    if (!open)
        return null;
    return createPortal(_jsxs("div", { className: "fixed inset-0 z-modal flex items-center justify-center p-4", onClick: handleOverlayClick, children: [_jsx(ModalOverlay, {}), _jsxs("div", { ref: panelRef, role: "dialog", "aria-modal": "true", "aria-label": ariaLabel, "aria-labelledby": ariaLabelledBy, tabIndex: -1, className: cn('relative z-10 w-full rounded-xl bg-white dark:bg-dark-800 shadow-modal', 'animate-modal-in', 'focus:outline-none', 'overflow-y-auto max-h-[calc(100vh-4rem)]', sizeClasses[size], className), onClick: (e) => e.stopPropagation(), children: [showCloseButton && (_jsx("button", { type: "button", onClick: onClose, className: "absolute top-3 right-3 p-1 rounded-md text-dark-400 hover:text-dark-600 dark:text-dark-500 dark:hover:text-dark-300 hover:bg-dark-100 dark:hover:bg-dark-700 transition-colors focus:outline-none focus:ring-2 focus:ring-primary-500/30 z-10", "aria-label": "Close dialog", children: _jsx(X, { className: "h-5 w-5" }) })), children] })] }), document.body);
};
Modal.displayName = 'Modal';
export const ModalHeader = forwardRef(({ children, className, ...props }, ref) => {
    return (_jsx("div", { ref: ref, className: cn('px-6 py-4 border-b border-dark-200 dark:border-dark-700', className), ...props, children: typeof children === 'string' ? (_jsx("h2", { className: "text-lg font-semibold text-dark-900 dark:text-dark-100", children: children })) : (children) }));
});
ModalHeader.displayName = 'ModalHeader';
export const ModalBody = forwardRef(({ children, className, ...props }, ref) => {
    return (_jsx("div", { ref: ref, className: cn('px-6 py-4', className), ...props, children: children }));
});
ModalBody.displayName = 'ModalBody';
export const ModalFooter = forwardRef(({ children, className, ...props }, ref) => {
    return (_jsx("div", { ref: ref, className: cn('px-6 py-4 border-t border-dark-200 dark:border-dark-700 flex items-center justify-end gap-3', className), ...props, children: children }));
});
ModalFooter.displayName = 'ModalFooter';
//# sourceMappingURL=Modal.js.map